import { supabase } from './supabase';

export interface AgentInfo {
  agent_id: string;
  display_name: string;
}

export interface Profile {
  id: string;
  email: string;
  created_at: string;
  is_active: boolean;
  full_name: string | null;
  company_name: string | null;
  role: 'user' | 'support' | 'admin';
  agents_info: AgentInfo[] | null;
}

export interface CallSummary {
  id: string;
  agent_id: string;
  call_id: string;
  call_time: string;
  call_duration: string;
  call_status: 'registered' | 'ongoing' | 'ended' | 'error';
  disconnection_reason?: string;
  user_sentiment?: 'Positive' | 'Neutral' | 'Negative';
  from_number: string;
  call_successful: boolean;
  call_summary: string;
  post_call_analysis_recommendations?: string;
  customer_name: string;
  customer_contact_number?: string;
  call_outcome_grade?: 'Excellent' | 'Good' | 'Average' | 'Poor';
  call_recording_url?: string;
  call_log_url?: string;
  notes?: string;
  transcript?: string;
}

export interface ContactSubmission {
  id: string;
  name: string;
  email: string;
  phone: string;
  message: string;
  created_at: string;
  status: 'pending' | 'contacted' | 'completed';
  notes?: string;
}

function rowToProfile(row: Record<string, unknown>): Profile {
  return {
    id: row.id as string,
    email: (row.email as string) || '',
    created_at: (row.created_at as string) || '',
    is_active: (row.is_active as boolean) ?? false,
    full_name: (row.full_name as string | null) ?? null,
    company_name: (row.company_name as string | null) ?? null,
    role: (row.role as Profile['role']) || 'user',
    agents_info: (row.agents_info as AgentInfo[] | null) ?? null,
  };
}

function rowToCallSummary(row: Record<string, unknown>): CallSummary {
  return {
    id: row.id as string,
    agent_id: (row.agent_id as string) || '',
    call_id: (row.call_id as string) || '',
    call_time: (row.call_time as string) || '',
    call_duration: (row.call_duration as string) || '',
    call_status: (row.call_status as CallSummary['call_status']) || 'ended',
    disconnection_reason: row.disconnection_reason as string | undefined,
    user_sentiment: row.user_sentiment as CallSummary['user_sentiment'],
    from_number: (row.from_number as string) || '',
    call_successful: (row.call_successful as boolean) ?? false,
    call_summary: (row.call_summary as string) || '',
    post_call_analysis_recommendations: row.post_call_analysis_recommendations as string | undefined,
    customer_name: (row.customer_name as string) || '',
    customer_contact_number: row.customer_contact_number as string | undefined,
    call_outcome_grade: row.call_outcome_grade as CallSummary['call_outcome_grade'],
    call_recording_url: row.call_recording_url as string | undefined,
    call_log_url: row.call_log_url as string | undefined,
    notes: row.notes as string | undefined,
    transcript: row.transcript as string | undefined,
  };
}

class DatabaseService {
  async getProfileByEmail(email: string): Promise<Profile | null> {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('email', email)
      .maybeSingle();
    return data ? rowToProfile(data) : null;
  }

  async getProfileById(uid: string): Promise<Profile | null> {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', uid)
      .maybeSingle();
    return data ? rowToProfile(data) : null;
  }

  async getAllProfiles(): Promise<Profile[]> {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false });
    return (data ?? []).map(rowToProfile);
  }

  async getUserProfiles(): Promise<Profile[]> {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('role', 'user');
    return (data ?? []).map(rowToProfile);
  }

  async getCallSummariesByAgents(agentIds: string[]): Promise<CallSummary[]> {
    if (agentIds.length === 0) return [];
    const { data } = await supabase
      .from('post_call_summaries')
      .select('*')
      .in('agent_id', agentIds)
      .order('call_time', { ascending: false });
    return (data ?? []).map(rowToCallSummary);
  }

  async getAllCallSummaries(): Promise<CallSummary[]> {
    const { data } = await supabase
      .from('post_call_summaries')
      .select('*')
      .order('call_time', { ascending: false });
    return (data ?? []).map(rowToCallSummary);
  }

  async updateCallNotes(callId: string, notes: string): Promise<void> {
    await supabase
      .from('post_call_summaries')
      .update({ notes })
      .eq('id', callId);
  }

  async getAllAgentDisplayNames(): Promise<Map<string, string>> {
    const { data } = await supabase.from('agents').select('agent_id, display_name');
    const map = new Map<string, string>();
    (data ?? []).forEach(row => {
      map.set(row.agent_id, row.display_name || `Agent ${row.agent_id.slice(0, 8)}`);
    });
    return map;
  }

  async submitContact(data: { name: string; email: string; phone: string; message: string }): Promise<void> {
    await supabase.from('contact_submissions').insert({
      ...data,
      status: 'pending',
    });
  }

  async updateProfile(profileId: string, updates: Partial<Omit<Profile, 'id'>>): Promise<void> {
    await supabase.from('profiles').update(updates).eq('id', profileId);
  }

  async addAgentToProfile(profileId: string, agent: AgentInfo): Promise<void> {
    const profile = await this.getProfileById(profileId);
    if (!profile) throw new Error('Profile not found');
    const existing = profile.agents_info || [];
    if (existing.some(a => a.agent_id === agent.agent_id)) return;
    await supabase
      .from('profiles')
      .update({ agents_info: [...existing, agent] })
      .eq('id', profileId);
  }

  async removeAgentFromProfile(profileId: string, agentId: string): Promise<void> {
    const profile = await this.getProfileById(profileId);
    if (!profile) throw new Error('Profile not found');
    const filtered = (profile.agents_info || []).filter(a => a.agent_id !== agentId);
    await supabase
      .from('profiles')
      .update({ agents_info: filtered })
      .eq('id', profileId);
  }

  async addAgent(agent: AgentInfo): Promise<void> {
    await supabase.from('agents').upsert(
      { agent_id: agent.agent_id, display_name: agent.display_name },
      { onConflict: 'agent_id' }
    );
  }

  async removeAgent(agentId: string): Promise<void> {
    await supabase.from('agents').delete().eq('agent_id', agentId);
  }

  async getAllAgents(): Promise<AgentInfo[]> {
    const { data } = await supabase.from('agents').select('agent_id, display_name');
    return (data ?? []).map(row => ({
      agent_id: row.agent_id,
      display_name: row.display_name || '',
    }));
  }
}

export const dbService = new DatabaseService();
